/**
 * Release 1 A — board-scoped filter predicates ("filter spine", docs/specs/release1-contract.md §A).
 *
 * The board filter bar, the search facet panel and saved views all speak `SearchFilters`; on a board the three
 * board-scope keys (`board_ids`, `include_descendants`, `include_deleted`) are owned by the route, so they are
 * stripped here and the API answers 422 if they leak through. Pure helpers only (URL round-trip, chips, presets).
 */
import type { ApprovalStatus, CustomField, Member, PersonCluster, SearchFilters, Tag } from '@/lib/api/types';
import { APPROVAL_TONE } from '@/components/Misc';
import { base64UrlDecode, base64UrlEncode } from '@/lib/format';

export const BOARD_SCOPE_KEYS = ['board_ids', 'include_descendants', 'include_deleted'] as const;
export type BoardScopeKey = (typeof BOARD_SCOPE_KEYS)[number];
export type BoardFilters = Omit<SearchFilters, BoardScopeKey>;

export const EMPTY_BOARD_FILTERS: BoardFilters = { tag_match: 'any' };

/** Keys that never count as "active" filters (defaults the API fills in anyway). */
const MODIFIER_KEYS = new Set<string>(['tag_match', 'include_archived', ...BOARD_SCOPE_KEYS]);

/** Drops board-scope keys, undefined/null, empty arrays and empty objects; normalises `tag_match`. */
export function compactBoardFilters(f: Partial<SearchFilters> | Partial<BoardFilters> | null | undefined): BoardFilters {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(f ?? {})) {
    if ((BOARD_SCOPE_KEYS as readonly string[]).includes(k)) continue;
    if (v === undefined || v === null || v === false) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    if (typeof v === 'object' && !Array.isArray(v)) {
      const inner = Object.fromEntries(Object.entries(v as Record<string, unknown>).filter(([, x]) => x !== undefined && x !== null && x !== ''));
      if (k === 'date') {
        if (!inner.from && !inner.to) continue;
        out[k] = { field: inner.field ?? 'created_at', ...inner };
        continue;
      }
      if (Object.keys(inner).length === 0) continue;
      out[k] = inner;
      continue;
    }
    if (k === 'tag_match' && v === 'any') continue;
    out[k] = v;
  }
  return { tag_match: 'any', ...(out as Partial<BoardFilters>) } as BoardFilters;
}

export function isEmptyBoardFilters(f: Partial<BoardFilters> | null | undefined): boolean {
  return countBoardFilters(f) === 0;
}

/** Number of active predicates (each array value counts once, each scalar/object once). */
export function countBoardFilters(f: Partial<BoardFilters> | null | undefined): number {
  const c = compactBoardFilters(f);
  let n = 0;
  for (const [k, v] of Object.entries(c)) {
    if (MODIFIER_KEYS.has(k)) continue;
    n += Array.isArray(v) ? v.length : 1;
  }
  return n;
}

/** Stable, order-independent JSON used for equality and query keys. */
export function canonicalFilters(f: Partial<BoardFilters> | null | undefined): string {
  const sortKeys = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(sortKeys);
    if (v && typeof v === 'object') return Object.fromEntries(Object.keys(v as object).sort().map((k) => [k, sortKeys((v as Record<string, unknown>)[k])]));
    return v;
  };
  return JSON.stringify(sortKeys(compactBoardFilters(f)));
}

export function sameFilters(a: Partial<BoardFilters> | null | undefined, b: Partial<BoardFilters> | null | undefined): boolean {
  return canonicalFilters(a) === canonicalFilters(b);
}

/** `?f=<base64url JSON>` — null when nothing is active so the URL stays clean. */
export function encodeBoardFilters(f: Partial<BoardFilters> | null | undefined): string | null {
  if (isEmptyBoardFilters(f)) return null;
  const c = compactBoardFilters(f);
  const { tag_match, ...rest } = c;
  return base64UrlEncode(JSON.stringify(tag_match === 'any' ? rest : c));
}

/** Tolerant inverse of `encodeBoardFilters`: malformed input yields the empty filter set. */
export function decodeBoardFilters(s: string | null | undefined): BoardFilters {
  if (!s) return { ...EMPTY_BOARD_FILTERS };
  try {
    const v = JSON.parse(base64UrlDecode(s)) as unknown;
    if (!v || typeof v !== 'object' || Array.isArray(v)) return { ...EMPTY_BOARD_FILTERS };
    return compactBoardFilters(v as Partial<SearchFilters>);
  } catch {
    return { ...EMPTY_BOARD_FILTERS };
  }
}

/** The JSON the API takes in `GET /boards/{id}/assets?filters=`; undefined when nothing is active (legacy path). */
export function filtersQueryParam(f: Partial<BoardFilters> | null | undefined): string | undefined {
  if (isEmptyBoardFilters(f)) return undefined;
  return JSON.stringify(compactBoardFilters(f));
}

/** Toggle a value inside an array predicate. */
export function toggleValue<K extends keyof BoardFilters>(f: BoardFilters, key: K, value: string): BoardFilters {
  const cur = (f[key] as unknown as string[] | undefined) ?? [];
  const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value];
  return { ...f, [key]: next };
}

// --- date presets ---------------------------------------------------------------------------------
export type DatePreset = 'today' | '7d' | '30d' | 'year' | 'custom';
export const DATE_PRESETS: ReadonlyArray<{ id: Exclude<DatePreset, 'custom'>; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: 'year', label: 'This year' },
];

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Local-day inclusive range for a preset (`to` is null = open-ended). */
export function presetRange(p: Exclude<DatePreset, 'custom'>, now = new Date()): { from: string; to: null } {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (p === '7d') d.setDate(d.getDate() - 6);
  else if (p === '30d') d.setDate(d.getDate() - 29);
  else if (p === 'year') {
    d.setMonth(0);
    d.setDate(1);
  }
  return { from: ymd(d), to: null };
}

/** Which preset a `date` predicate corresponds to today (null when no date filter is set). */
export function datePresetOf(date: BoardFilters['date'] | undefined, now = new Date()): DatePreset | null {
  if (!date || (!date.from && !date.to)) return null;
  if (date.to) return 'custom';
  for (const p of DATE_PRESETS) if (presetRange(p.id, now).from === date.from) return p.id;
  return 'custom';
}

// --- chips ----------------------------------------------------------------------------------------
export type ChipContext = { tags?: readonly Tag[]; fields?: readonly CustomField[]; people?: readonly PersonCluster[]; members?: readonly Member[]; now?: Date };
export type FilterChip = {
  /** predicate group the chip belongs to (matches the filter bar buttons) */
  group: 'types' | 'approval' | 'tag_ids' | 'colors' | 'date' | 'people' | 'custom_fields' | 'orientation' | 'uploaded_by' | 'objects' | 'other';
  /** stable id, unique across chips */
  id: string;
  label: string;
  /** filters with this one predicate removed */
  remove: (f: BoardFilters) => BoardFilters;
};

const TYPE_LABEL: Record<string, string> = { image: 'Images', video: 'Videos', audio: 'Audio', document: 'Documents', other: 'Other' };
const ORIENTATION_LABEL: Record<string, string> = { landscape: 'Landscape', portrait: 'Portrait', square: 'Square' };
const OP_LABEL: Record<string, string> = { eq: '=', in: 'in', contains: 'contains', gte: '≥', lte: '≤', between: 'between' };

export function typeLabel(t: string): string {
  return TYPE_LABEL[t] ?? t;
}
export function approvalLabel(a: string): string {
  return APPROVAL_TONE[a as ApprovalStatus]?.label ?? a;
}

/** One removable chip per active predicate value, in filter-bar order. */
export function filterChips(f: Partial<BoardFilters> | null | undefined, ctx: ChipContext = {}): FilterChip[] {
  const c = compactBoardFilters(f);
  const out: FilterChip[] = [];
  const dropFrom = <K extends keyof BoardFilters>(key: K, value: string) => (cur: BoardFilters) => ({ ...cur, [key]: ((cur[key] as unknown as string[] | undefined) ?? []).filter((x) => x !== value) });
  for (const t of c.types ?? []) out.push({ group: 'types', id: `types:${t}`, label: `Type: ${typeLabel(t)}`, remove: dropFrom('types', t) });
  for (const a of c.approval ?? []) out.push({ group: 'approval', id: `approval:${a}`, label: `Approval: ${approvalLabel(a)}`, remove: dropFrom('approval', a) });
  for (const id of c.tag_ids ?? []) out.push({ group: 'tag_ids', id: `tag:${id}`, label: `Tag: ${ctx.tags?.find((t) => t.id === id)?.name ?? id.slice(0, 8)}`, remove: dropFrom('tag_ids', id) });
  for (const col of c.colors ?? []) out.push({ group: 'colors', id: `color:${col.hex.toLowerCase()}`, label: `Colour: ${col.hex.toLowerCase()}`, remove: (cur) => ({ ...cur, colors: (cur.colors ?? []).filter((x) => x.hex.toLowerCase() !== col.hex.toLowerCase()) }) });
  if (c.date && (c.date.from || c.date.to)) {
    const preset = datePresetOf(c.date, ctx.now);
    const field = c.date.field?.startsWith('custom:') ? (ctx.fields?.find((x) => x.id === c.date!.field.slice(7))?.name ?? 'Field') : c.date.field === 'uploaded_at' ? 'Uploaded' : 'Created';
    const when = preset && preset !== 'custom' ? DATE_PRESETS.find((p) => p.id === preset)!.label.toLowerCase() : c.date.from && c.date.to ? `${c.date.from} – ${c.date.to}` : c.date.from ? `since ${c.date.from}` : `until ${c.date.to}`;
    out.push({ group: 'date', id: 'date', label: `${field}: ${when}`, remove: (cur) => ({ ...cur, date: undefined }) });
  }
  for (const id of c.people ?? []) out.push({ group: 'people', id: `person:${id}`, label: `Person: ${ctx.people?.find((p) => p.id === id)?.name ?? 'Unnamed'}`, remove: dropFrom('people', id) });
  for (const id of c.uploaded_by ?? []) out.push({ group: 'uploaded_by', id: `uploader:${id}`, label: `Uploaded by: ${ctx.members?.find((m) => m.id === id)?.user.display_name ?? id.slice(0, 8)}`, remove: dropFrom('uploaded_by', id) });
  (c.custom_fields ?? []).forEach((cf, i) => {
    const field = ctx.fields?.find((x) => x.id === cf.field_id);
    const value = Array.isArray(cf.value) ? cf.value.map((v) => optionLabel(field, String(v))).join(', ') : optionLabel(field, String(cf.value));
    out.push({ group: 'custom_fields', id: `field:${cf.field_id}:${i}`, label: `${field?.name ?? 'Field'} ${OP_LABEL[cf.op] ?? cf.op} ${value}`, remove: (cur) => ({ ...cur, custom_fields: (cur.custom_fields ?? []).filter((_, j) => j !== i) }) });
  });
  for (const o of c.orientation ?? []) out.push({ group: 'orientation', id: `orientation:${o}`, label: `Orientation: ${ORIENTATION_LABEL[o] ?? o}`, remove: dropFrom('orientation', o) });
  for (const o of c.objects ?? []) out.push({ group: 'objects', id: `object:${o}`, label: `Object: ${o}`, remove: dropFrom('objects', o) });
  if (c.has_transcript) out.push({ group: 'other', id: 'has_transcript', label: 'Has transcript', remove: (cur) => ({ ...cur, has_transcript: undefined }) });
  if (c.min_width) out.push({ group: 'other', id: 'min_width', label: `Width ≥ ${c.min_width}px`, remove: (cur) => ({ ...cur, min_width: undefined }) });
  if (c.min_height) out.push({ group: 'other', id: 'min_height', label: `Height ≥ ${c.min_height}px`, remove: (cur) => ({ ...cur, min_height: undefined }) });
  if (c.duration_ms?.min != null || c.duration_ms?.max != null) {
    const d = c.duration_ms;
    out.push({ group: 'other', id: 'duration', label: `Duration ${d.min != null ? `≥ ${d.min / 1000}s` : ''}${d.min != null && d.max != null ? ' ' : ''}${d.max != null ? `≤ ${d.max / 1000}s` : ''}`.trim(), remove: (cur) => ({ ...cur, duration_ms: undefined }) });
  }
  return out;
}

function optionLabel(field: CustomField | undefined, v: string): string {
  return field?.options?.find((o) => o.id === v)?.label ?? v;
}
